import { supabase } from '../lib/supabase'

export interface Announcement {
  id: string
  church_id: string
  title: string
  content: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  target_groups?: string[]
  target_roles?: string[]
  start_date: string
  end_date?: string
  is_pinned: boolean
  author_id: string
  attachments?: any[]
  created_at: string
  updated_at: string
  author?: {
    first_name: string
    last_name: string
  }
}

export interface SMSTemplate {
  id: string
  name: string
  content: string
  category: string
  variables: string[]
}

export interface SMSCampaign {
  id: string
  name: string
  message: string
  recipients: string[]
  scheduled_at?: string
  sent_at?: string
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed'
  total_recipients: number
  successful: number
  failed: number
}

export interface CommunicationStats {
  total_announcements: number
  active_announcements: number
  total_sms_sent: number
  sms_success_rate: number
  recent_activity: any[]
}

// Announcements
export const getAnnouncements = async (churchId: string) => {
  const { data, error } = await supabase
    .from('announcements')
    .select(`
      *,
      author:profiles!announcements_author_id_fkey(first_name, last_name)
    `)
    .eq('church_id', churchId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as Announcement[]
}

export const createAnnouncement = async (announcement: Partial<Announcement>) => {
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('announcements')
    .insert({
      ...announcement,
      author_id: user?.id
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export const updateAnnouncement = async (id: string, updates: Partial<Announcement>) => {
  const { data, error } = await supabase
    .from('announcements')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export const deleteAnnouncement = async (id: string) => {
  const { error } = await supabase
    .from('announcements')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// SMS Logs and Statistics
export const getSMSLogs = async (limit = 100) => {
  try {
    const { data, error } = await supabase
      .from('sms_logs')
      .select('*')
      .order('sent_at', { ascending: false })
      .limit(limit)

    if (error) {
      // Table doesn't exist yet - return empty array
      if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
        console.warn('sms_logs table does not exist yet')
        return []
      }
      throw error
    }
    return data || []
  } catch (error) {
    console.warn('Error fetching SMS logs:', error)
    return []
  }
}

export const getSMSStatistics = async () => {
  try {
    const { data, error } = await supabase
      .from('sms_logs')
      .select('status')

    if (error) {
      // Table doesn't exist yet - return default stats
      if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
        console.warn('sms_logs table does not exist yet')
        return {
          total: 0,
          successful: 0,
          failed: 0,
          pending: 0,
          success_rate: 0
        }
      }
      throw error
    }

    const total = data?.length || 0
    const successful = data?.filter(log => log.status === 'sent').length || 0
    const failed = data?.filter(log => log.status === 'failed').length || 0
    const pending = data?.filter(log => log.status === 'pending').length || 0

    return {
      total,
      successful,
      failed,
      pending,
      success_rate: total > 0 ? (successful / total) * 100 : 0
    }
  } catch (error) {
    console.warn('Error fetching SMS statistics:', error)
    return {
      total: 0,
      successful: 0,
      failed: 0,
      pending: 0,
      success_rate: 0
    }
  }
}

// Send Bulk SMS
export const sendBulkSMS = async (
  recipients: string[],
  message: string,
  campaignName?: string
) => {
  const results = {
    successful: 0,
    failed: 0,
    errors: [] as string[]
  }

  for (const phone of recipients) {
    try {
      const apiKey = import.meta.env.VITE_MNOTIFY_API_KEY
      const senderId = import.meta.env.VITE_MNOTIFY_SENDER_ID || 'FaithYoke'

      if (!apiKey) {
        console.warn('MNotify API key not configured - SMS will not be sent')
        results.push({ phone, success: false, error: 'SMS service not configured' })
        continue
      }

      // Format phone number for MNotify (remove + and spaces)
      const formattedPhone = phone.replace(/[\s+]/g, '')

      const response = await fetch(
        `https://api.mnotify.com/api/sms/quick?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient: [formattedPhone],
            sender: senderId,
            message: message,
            is_schedule: false
          })
        }
      )

      const result = await response.json()

      if (result.code === '2000' || response.ok) {
        results.successful++

        // Log to database
        await supabase.from('sms_logs').insert({
          to_phone: phone,
          message: message,
          status: 'sent',
          environment: 'production'
        })
      } else {
        results.failed++
        results.errors.push(`${phone}: ${result.message || 'Unknown error'}`)

        // Log failure
        await supabase.from('sms_logs').insert({
          to_phone: phone,
          message: message,
          status: 'failed',
          error_message: result.message || 'Unknown error',
          environment: 'production'
        })
      }
    } catch (error: any) {
      results.failed++
      results.errors.push(`${phone}: ${error.message}`)

      // Log failure
      await supabase.from('sms_logs').insert({
        to_phone: phone,
        message: message,
        status: 'failed',
        error_message: error.message,
        environment: 'production'
      })
    }
  }

  return results
}

// Get members for SMS targeting
export const getMembersForSMS = async (churchId: string, filters?: {
  groups?: string[]
  roles?: string[]
}) => {
  let query = supabase
    .from('profiles')
    .select('id, first_name, last_name, phone, role')
    .eq('church_id', churchId)
    .not('phone', 'is', null)

  if (filters?.roles && filters.roles.length > 0) {
    query = query.in('role', filters.roles)
  }

  const { data, error } = await query

  if (error) throw error

  // If group filter is specified, filter by group membership
  if (filters?.groups && filters.groups.length > 0 && data) {
    const { data: groupMembers } = await supabase
      .from('group_members')
      .select('member_id')
      .in('group_id', filters.groups)

    const memberIds = new Set(groupMembers?.map(gm => gm.member_id))

    // Get member profile_ids
    const { data: members } = await supabase
      .from('members')
      .select('profile_id')
      .in('id', Array.from(memberIds))

    const profileIds = new Set(members?.map(m => m.profile_id))

    return data.filter(profile => profileIds.has(profile.id))
  }

  return data
}

// Communication Statistics Dashboard
export const getCommunicationStats = async (churchId: string): Promise<CommunicationStats> => {
  // Get announcement stats
  const { data: announcements } = await supabase
    .from('announcements')
    .select('id, start_date, end_date')
    .eq('church_id', churchId)

  const now = new Date().toISOString().split('T')[0]
  const activeAnnouncements = announcements?.filter(a => {
    const inDateRange = a.start_date <= now && (!a.end_date || a.end_date >= now)
    return inDateRange
  })

  // Get SMS stats
  const smsStats = await getSMSStatistics()

  // Get recent activity
  const { data: recentActivity } = await supabase
    .from('announcements')
    .select('id, title, created_at')
    .eq('church_id', churchId)
    .order('created_at', { ascending: false })
    .limit(5)

  return {
    total_announcements: announcements?.length || 0,
    active_announcements: activeAnnouncements?.length || 0,
    total_sms_sent: smsStats.total,
    sms_success_rate: smsStats.success_rate,
    recent_activity: recentActivity || []
  }
}

// SMS Templates
const defaultTemplates: SMSTemplate[] = [
  {
    id: 'welcome',
    name: 'Welcome Message',
    content: 'Welcome to {church_name}, {name}! We are blessed to have you in our fellowship. Service times: Sundays at {service_time}.',
    category: 'onboarding',
    variables: ['church_name', 'name', 'service_time']
  },
  {
    id: 'event_reminder',
    name: 'Event Reminder',
    content: 'Hi {name}, reminder: {event_name} is tomorrow at {event_time}. Location: {event_location}. See you there!',
    category: 'events',
    variables: ['name', 'event_name', 'event_time', 'event_location']
  },
  {
    id: 'contribution_thanks',
    name: 'Contribution Thank You',
    content: 'Thank you {name} for your generous contribution of {amount}. Your faithfulness supports God\'s work. Receipt: {receipt_number}',
    category: 'finance',
    variables: ['name', 'amount', 'receipt_number']
  },
  {
    id: 'birthday_wish',
    name: 'Birthday Wish',
    content: 'Happy Birthday {name}! May God bless you abundantly in this new year. The {church_name} family celebrates with you today!',
    category: 'special_occasions',
    variables: ['name', 'church_name']
  },
  {
    id: 'prayer_update',
    name: 'Prayer Request Update',
    content: 'Hi {name}, update on your prayer request: {update}. We continue to lift you up in prayer. - {church_name}',
    category: 'pastoral_care',
    variables: ['name', 'update', 'church_name']
  },
  {
    id: 'emergency_alert',
    name: 'Emergency Alert',
    content: 'URGENT: {message}. Please contact {contact_person} at {contact_phone} for details. - {church_name}',
    category: 'urgent',
    variables: ['message', 'contact_person', 'contact_phone', 'church_name']
  },
  {
    id: 'service_cancellation',
    name: 'Service Cancellation',
    content: 'Important: {service_name} on {date} has been cancelled. Reason: {reason}. Next service: {next_service}. - {church_name}',
    category: 'announcements',
    variables: ['service_name', 'date', 'reason', 'next_service', 'church_name']
  }
]

export const getSMSTemplates = () => {
  return defaultTemplates
}

export const renderTemplate = (template: SMSTemplate, variables: Record<string, string>) => {
  let message = template.content
  template.variables.forEach(variable => {
    const value = variables[variable] || `{${variable}}`
    message = message.replace(new RegExp(`{${variable}}`, 'g'), value)
  })
  return message
}
